import { Link } from 'react-router-dom';
import { usePageMeta } from '../hooks/usePageMeta';

const LAST_UPDATED = 'May 31, 2026';

export default function Privacy() {
  usePageMeta(
    'Privacy Policy | Helpped',
    'Helpped privacy policy — how we collect, use, and protect your data.',
  );
  return (
    <div className="min-h-screen flex flex-col">
      <nav className="sticky top-0 z-50 bg-white/90 backdrop-blur border-b border-gray-100">
        <div className="max-w-5xl mx-auto px-6 h-14 flex items-center gap-3">
          <Link to="/" className="flex items-center gap-2 group">
            <div
              className="w-7 h-7 rounded-lg flex items-center justify-center text-white font-bold text-sm"
              style={{ background: 'hsl(var(--primary))' }}
            >
              H
            </div>
            <span className="font-bold text-gray-900 tracking-tight group-hover:opacity-80 transition-opacity">
              Helpped
            </span>
          </Link>
          <span className="text-gray-300">/</span>
          <span className="text-sm text-gray-500">Privacy Policy</span>
        </div>
      </nav>

      <main className="flex-1 px-6 py-12">
        <div className="max-w-2xl mx-auto prose prose-gray">
          <h1 className="text-3xl font-extrabold text-gray-900 mb-1">Privacy Policy</h1>
          <p className="text-sm text-gray-400 mb-10">Last updated: {LAST_UPDATED}</p>

          <Section title="1. What we collect">
            <p>When you use Helpped, we may collect:</p>
            <ul>
              <li><strong>Location data</strong> — approximate or precise GPS coordinates to show you nearby content and let you post to your neighborhood. Location is only used while the app is open unless you grant "Always On" permission, which you can revoke at any time in your device settings.</li>
              <li><strong>Account information</strong> — email address or phone number if you choose to create an account or join our waitlist.</li>
              <li><strong>User content</strong> — posts, photos, and messages you share within the app.</li>
              <li><strong>Device and usage data</strong> — device type, OS version, crash logs, and anonymized usage events to improve performance.</li>
            </ul>
          </Section>

          <Section title="2. How we use your information">
            <p>We use the data we collect to:</p>
            <ul>
              <li>Display neighborhood-relevant posts, events, and civic updates near your location.</li>
              <li>Allow neighbors to connect and communicate through the app.</li>
              <li>Send you notifications you've opted into (e.g., waitlist updates, nearby alerts).</li>
              <li>Improve app performance, fix bugs, and develop new features.</li>
              <li>Comply with applicable laws and enforce our Terms of Service.</li>
            </ul>
            <p>We do <strong>not</strong> sell your personal information to third parties.</p>
          </Section>

          <Section title="3. How we share your information">
            <p>We share data only in these limited circumstances:</p>
            <ul>
              <li><strong>With our service providers</strong> — Supabase (database and authentication), AWS (hosting), and similar infrastructure partners who process data on our behalf under data processing agreements.</li>
              <li><strong>As required by law</strong> — if required by a court order, subpoena, or other legal obligation.</li>
              <li><strong>In a business transfer</strong> — if Helpped is acquired or merges with another company, your data may transfer as part of that transaction. We will notify you beforehand.</li>
            </ul>
          </Section>

          <Section title="4. Public content">
            <p>Posts and content you share on Helpped are visible to other users in your neighborhood. Do not post information you want kept private. You can delete your posts at any time from within the app.</p>
          </Section>

          <Section title="5. Data retention">
            <p>We retain your personal data for as long as your account is active. You can request deletion of your account and associated data by emailing <a href="mailto:privacy@helpped.com" className="text-green-700 hover:underline">privacy@helpped.com</a>. We will process deletion requests within 30 days.</p>
          </Section>

          <Section title="6. Children">
            <p>Helpped is not directed at children under 13. We do not knowingly collect personal information from children under 13. If you believe a child has provided us with personal information, please contact us and we will delete it.</p>
          </Section>

          <Section title="7. Your rights">
            <p>Depending on your location, you may have the right to access, correct, delete, or export your personal data. To exercise these rights, contact us at <a href="mailto:privacy@helpped.com" className="text-green-700 hover:underline">privacy@helpped.com</a>.</p>
          </Section>

          <Section title="8. Changes to this policy">
            <p>We may update this Privacy Policy from time to time. We'll post the updated policy here and, for material changes, notify you via email or in-app notification. Continued use of Helpped after changes take effect constitutes acceptance of the updated policy.</p>
          </Section>

          <Section title="9. Contact">
            <p>Questions about this policy? Email us at <a href="mailto:privacy@helpped.com" className="text-green-700 hover:underline">privacy@helpped.com</a>.</p>
          </Section>
        </div>
      </main>

      <footer className="px-6 py-8 border-t border-gray-100">
        <div className="max-w-5xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-4 text-sm text-gray-400">
          <span>© {new Date().getFullYear()} Helpped. Auburn, WA.</span>
          <div className="flex gap-6">
            <Link to="/" className="hover:text-gray-600 transition-colors">Home</Link>
            <Link to="/terms" className="hover:text-gray-600 transition-colors">Terms</Link>
          </div>
        </div>
      </footer>
    </div>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section className="mb-8">
      <h2 className="text-lg font-bold text-gray-900 mb-3">{title}</h2>
      <div className="text-gray-600 space-y-3 leading-relaxed text-sm">{children}</div>
    </section>
  );
}
